import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import { PageOrThread } from '@/components/admin/experiences/conversation-thread';
import {
  AddPageMenu,
  type AddPageMenuTrigger,
} from '@/components/admin/experiences/timeline/add-page-menu';
import { ConversationBadge } from '@/components/admin/experiences/timeline/conversation-badge';
import { FeedbackBadge } from '@/components/admin/experiences/timeline/feedback-badge';
import type { ReaderFormat } from '@/components/reader';
import { Tooltip } from '@/components/ui/tooltip';
import { edgeInThread } from '@/lib/experience-draft';
import type {
  ExperiencePageDraft,
  ExperienceTheme,
  ExperienceVariable,
} from '@matter_extractor/shared';
import { EXPERIENCE_PAGES_MAX, isConversationPage } from '@matter_extractor/shared';
import { Plus } from 'lucide-react';
import { Fragment, useEffect, useRef } from 'react';

/**
 * Le rail de pastilles (refonte modulaire §9.1 ; maquette `Timeline.dc.html` ;
 * issues #57 et #140).
 *
 * « Un rail de pastilles numérotées, la page courante remplie, "Ajouter une
 * page" en pointillé au bout : il dit où l'on est et mène à n'importe quelle
 * page. » It is the rail of the second proposition of the 11th of September,
 * kept with the scene of the third (revue du 2026-09-11, seconde passe).
 *
 * ## What it is FOR, beside the scene that already shows three pages
 *
 * The scene shows a page and its two neighbours; the rail shows the WHOLE line
 * at once. On an experience of nine pages that is the only surface that answers
 * « où suis-je » and « combien en reste-t-il », and it is the only way to reach
 * page 7 from page 2 without walking through five.
 *
 * ## DEUX hôtes depuis #140, et un seul dessin
 *
 * That sentence is exactly the defect Alice reported on the 2026-09-23: the
 * page editor's banner led to the two NEIGHBOURS only, so page 1 to page 7 cost
 * six clicks on « Suivante ». The component that answers it already existed -
 * this one - and it was mounted in the Timeline tab alone. So the banner mounts
 * it too, and the variant is a PROP rather than a second rail: two friezes
 * drawn separately would end up disagreeing about what a page looks like, and
 * the order an operator learns on one screen would stop reading on the other.
 *
 * `compact` is what the banner passes, and what it changes is stated on the
 * interface below. Absent - the Timeline - the rail is the one of #57 to the
 * node: every pastille named, « Ajouter une page » in dashes at the far end.
 *
 * ## Il défile DANS SON CADRE, des deux côtés
 *
 * Thirty pages is the schema's ceiling, and thirty pastilles do not fit any
 * width - named or not. The rule of the root `CLAUDE.md`: wide content scrolls
 * in its own container, the page body never does, which is also what
 * `admin-responsive.spec.ts` holds every administration screen to.
 *
 * `mx-auto` under `compact` centres the line while it fits, and resolves to
 * zero the moment it does not - an over-constrained auto margin is treated as
 * zero, so the line starts at the left, which is where a scroll must start. The
 * scene of #57 makes the same arbitrage for its row of three pages.
 *
 * ## La page de retour (retour-experience.md §7.1, issue #176)
 *
 * Its pastille carries « Retour » in its label, and « Ajouter une page » passes
 * JUST BEFORE it - « c'est là qu'il insère, et rien ne se pose après le retour »
 * - instead of closing the line. While the experience has no feedback page
 * (`onAddFeedbackPage` present) the button opens the menu « Page de contenu » /
 * « Page de retour » of the scene's « + » of the end, the same `AddPageMenu`.
 */

/**
 * Ce que le bandeau de l'éditeur de page passe, et ce que ça change (#140).
 *
 * Its presence IS the variant - there is no second flag, and no `variant`
 * string - because the three things it changes all need the same three values,
 * and apart they would be props that only make sense together:
 *
 *   - a pastille carries its RANK ALONE, except the current page, which keeps
 *     its name in clear. In a narrow middle column that shows ten of them at a
 *     glance instead of two or three, and the banner already names the previous
 *     and the next page in full on either side;
 *   - NO « Ajouter une page »: the banner has its two « + », which aim at a
 *     PRECISE side, and a third add button would be the same gesture, less
 *     precise;
 *   - the current pastille is SCROLLED INTO VIEW, and a bubble carries the
 *     page's name and its thumbnail - see each below.
 *
 * The three values are what the bubble's thumbnail needs, and `PageNav` holds
 * all three already: it renders the very same reader for the « Suivante »
 * thumbnail.
 */
export interface ExperienceRailCompact {
  /** Le thème de l'expérience : la vignette peint la page comme le lecteur (§9.4). */
  readonly theme: ExperienceTheme;
  readonly format: ReaderFormat;
  /** Ce que la vignette NOMME - jamais une valeur inventée (§8.6, D5). */
  readonly variableNames: (variable: ExperienceVariable) => string;
}

export interface ExperienceRailProps {
  readonly pages: readonly ExperiencePageDraft[];
  readonly selectedPageId: string;
  readonly readOnly: boolean;
  readonly onSelectPage: (pageId: string) => void;
  /**
   * « Ajouter une page » at the far end - one page appended to the line (§9.1).
   *
   * Optional since #140, and absent under `compact`: the banner draws no add
   * button, so a callback it could never reach would be a lie in the signature.
   */
  readonly onAddPage?: (() => void) | undefined;
  /**
   * « Page de retour » of the menu of « Ajouter une page » (§7.1, #176) - present
   * only while the experience HAS NO feedback page. Then the button opens the
   * menu, whose « Page de contenu » is `onAddPage`.
   */
  readonly onAddFeedbackPage?: (() => void) | undefined;
  /** Why « Page de retour » is closed - the ceiling of 30 pages - or null. */
  readonly feedbackRefusal?: string | null | undefined;
  /** Absente : le rail NOMMÉ de la Timeline. Présente : celui du bandeau (#140). */
  readonly compact?: ExperienceRailCompact | undefined;
}

/**
 * Ce que la bulle d'une pastille dit : le nom de la page, et sa vignette.
 *
 * ## La vignette est le VRAI lecteur, en `static`
 *
 * The same reasoning `page-nav.tsx` gives for the « Suivante » thumbnail, and
 * the same component: a thumbnail drawn any other way would be a second
 * renderer of a page, the very thing §9.2 forbids. It is rendered at its
 * author's size and scaled by a transform, so it looks like the page and not
 * like a narrower page. Bigger here than in the banner - 192 x 132 against
 * 64 x 44 - because a bubble has the room, and the 64 x 44 stamp of
 * « Suivante » does not move.
 *
 * ## Elle n'est montée qu'à l'OUVERTURE de la bulle
 *
 * A React element is a description until something renders it, and `Tooltip`
 * renders its `label` only while the bubble is open. So thirty pastilles cost
 * thirty descriptors and ZERO readers at rest - three readers standing in a
 * banner is the cost this lot refused, and it is why the thumbnail lives in the
 * bubble rather than in the pastille.
 */
function RailBubble({
  page,
  name,
  compact,
}: {
  readonly page: ExperiencePageDraft;
  readonly name: string;
  readonly compact: ExperienceRailCompact;
}) {
  return (
    <>
      <div className="w-48 truncate font-semibold text-gray-900">{name}</div>
      <div className="mt-1.5 h-33 w-48 overflow-hidden rounded-sm border border-gray-200">
        {/* The real reader at three quarters - see the head of this component. */}
        <div className="h-44 w-64 origin-top-left scale-75">
          <PageOrThread
            page={page}
            theme={compact.theme}
            format={compact.format}
            mode="static"
            values={{}}
            variableNames={compact.variableNames}
          />
        </div>
      </div>
    </>
  );
}

export function ExperienceRail({
  pages,
  selectedPageId,
  readOnly,
  onSelectPage,
  onAddPage,
  onAddFeedbackPage,
  feedbackRefusal = null,
  compact,
}: ExperienceRailProps) {
  const full = pages.length >= EXPERIENCE_PAGES_MAX;
  /** The feedback page is the last, and « Ajouter une page » stands before it (§7.1). */
  const feedbackAt = pages.findIndex((page) => page.kind === 'feedback');
  const here = useRef<HTMLButtonElement | null>(null);
  const scrolls = compact !== undefined;

  /**
   * La pastille courante est AMENÉE À LA VUE à chaque changement de page (#140).
   *
   * Not cosmetic: on page 23 of an experience of 30, a rail that keeps its
   * scroll at the beginning opens with the current pastille OUT OF SIGHT, and
   * the one surface that exists to answer « où suis-je » stops answering it.
   *
   * `block: 'nearest'` touches the vertical axis only when the pastille is not
   * already on it - the banner sits at the top of the screen, and a rail that
   * scrolled the page on every navigation would move what somebody was reading.
   *
   * Under `compact` ONLY: the Timeline keeps the rail of #57, whose frame sits
   * under a scene of 400 px that this very `block: 'nearest'` could scroll out
   * from under a capture script.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger, not read.
  useEffect(() => {
    if (!scrolls) return;
    // Optional call: jsdom has no `scrollIntoView`.
    here.current?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
  }, [scrolls, selectedPageId]);

  const addPageButton = (props: Partial<AddPageMenuTrigger>) => (
    <button
      type="button"
      data-testid={EXPERIENCE_IDS.addPage}
      onClick={onAddPage}
      {...props}
      className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-pill border border-bleu-nuit border-dashed bg-white px-3 font-semibold text-bleu-nuit text-sm leading-5 hover:bg-bleu-nuit-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Plus aria-hidden="true" className="size-3.5" />
      Ajouter une page
    </button>
  );

  /*
    « Ajouter une page » exists on the Timeline only: the banner has its two
    « + », which aim at a precise side - see `ExperienceRailCompact`. It stands
    JUST BEFORE the feedback page when there is one (§7.1), and opens the menu
    « Page de contenu » / « Page de retour » while there is none.
  */
  const addPageItem =
    readOnly || full || compact !== undefined || onAddPage === undefined ? null : (
      <li key="add-page" className="flex items-center">
        <span aria-hidden="true" className="block h-0.5 w-5 shrink-0 bg-rail" />
        {onAddFeedbackPage === undefined ? (
          addPageButton({})
        ) : (
          <AddPageMenu
            label="Ajouter une page"
            contentHint={`Une page de blocs, après « ${pages[pages.length - 1]?.name ?? ''} »`}
            contentRefusal={null}
            feedbackRefusal={feedbackRefusal}
            onContent={onAddPage}
            onFeedback={onAddFeedbackPage}
            trigger={(props) => addPageButton(props)}
          />
        )}
      </li>
    );

  return (
    <div className={compact === undefined ? 'space-y-2' : 'w-full min-w-0'}>
      <div data-testid={EXPERIENCE_IDS.rail} className="overflow-x-auto pb-1">
        <ol className={`flex w-max items-center ${compact === undefined ? '' : 'mx-auto'}`}>
          {pages.map((page, index) => {
            const selected = page.id === selectedPageId;
            const name = page.name === '' ? 'Page sans nom' : page.name;
            /*
              The name in clear: on every pastille of the Timeline, on the
              current page alone in the banner. The rank stays, on both sides.
            */
            const named = compact === undefined || selected;

            const pastille = (
              <button
                type="button"
                ref={selected ? here : null}
                data-testid={EXPERIENCE_IDS.railPage}
                data-kind={page.kind}
                data-current={selected ? 'true' : 'false'}
                {...(selected ? { 'aria-current': 'page' as const } : {})}
                /*
                  The accessible name, for a pastille that paints its rank alone.

                  The drawn rank is `aria-hidden` - a visual marker the name
                  doubles everywhere else - so without this label a compact
                  pastille would have NO name at all. It carries both facts, and
                  it carries them on the current page too, whose visible name it
                  contains. It is what keeps the pastille usable where no bubble
                  opens: at the touch, and at the keyboard.
                */
                {...(compact === undefined ? {} : { 'aria-label': `Page ${index + 1} - ${name}` })}
                onClick={() => onSelectPage(page.id)}
                className={`inline-flex h-8 cursor-pointer items-center rounded-pill border py-0 font-semibold text-sm leading-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  named ? 'gap-2 pr-3 pl-1.5' : 'px-1.5'
                } ${
                  selected
                    ? 'border-bleu-nuit bg-bleu-nuit text-white'
                    : 'border-gray-200 bg-white text-gray-800 hover:border-bleu-nuit hover:text-bleu-nuit'
                }`}
              >
                {/* `tabular-nums` so a line of ten pages keeps one left edge
                    for its names - the reason the protocol's chain gives. */}
                <span
                  aria-hidden="true"
                  className={`inline-flex size-5 shrink-0 items-center justify-center rounded-full text-xs tabular-nums ${
                    selected ? 'bg-white text-bleu-nuit' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {index + 1}
                </span>
                {named ? <span className="max-w-40 truncate">{name}</span> : null}
                {named && page.kind === 'feedback' ? <FeedbackBadge /> : null}
                {named && isConversationPage(page) ? (
                  <ConversationBadge onFilled={selected} className="py-0 pr-1.5 pl-1 text-[11px]" />
                ) : null}
              </button>
            );

            return (
              <Fragment key={page.id}>
                {index === feedbackAt ? addPageItem : null}
                <li className="flex items-center">
                  {index === 0 ? null : (
                    <span
                      aria-hidden="true"
                      data-in-thread={edgeInThread(pages, index) ? 'true' : 'false'}
                      className={`block h-0.5 w-5 shrink-0 ${
                        edgeInThread(pages, index) ? 'bg-bleu-nuit' : 'bg-rail'
                      }`}
                    />
                  )}
                  {compact === undefined ? (
                    pastille
                  ) : (
                    /*
                    `describes={false}`: the trigger ALREADY carries the page's
                    name in its `aria-label`, and the bubble repeats it for the
                    eye. Without that false a screen reader would read the name
                    twice - and the component cannot see it by itself, its own
                    comparison bearing on a label of type string (issue 104).

                    `side="bottom"`: the banner sits at the top of the screen, so
                    a bubble this tall would flip there anyway.
                  */
                    <Tooltip
                      label={<RailBubble page={page} name={name} compact={compact} />}
                      side="bottom"
                      describes={false}
                      data-testid={EXPERIENCE_IDS.railBubble}
                    >
                      {pastille}
                    </Tooltip>
                  )}
                </li>
              </Fragment>
            );
          })}

          {/* On a line with no feedback page, « Ajouter une page » closes the line. */}
          {feedbackAt === -1 ? addPageItem : null}
        </ol>
      </div>

      {/*
        The ceiling sentence explains the dashed pastille that has just left.
        Under `compact` no pastille leaves, and the banner's two « + » say the
        ceiling their own way, by closing.
      */}
      {full && compact === undefined ? (
        <p className="text-gray-600 text-xs leading-4">
          Une expérience porte au plus {EXPERIENCE_PAGES_MAX} pages.
        </p>
      ) : null}
    </div>
  );
}
